import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { RingLoader } from 'react-spinners';
import { useNetworkStats } from '../hooks/useNetworkStats';

function formatNumber(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-US');
}

function AnimatedStat({ label, value, suffix = '', inView }) {
  const [display, setDisplay] = useState(0);
  const target = Number(value) || 0;

  useEffect(() => {
    if (!inView || target === 0) {
      setDisplay(target);
      return undefined;
    }

    const duration = 1200;
    const start = performance.now();

    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(Math.round(target * eased));
      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    const frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, target]);

  return (
    <div className="text-center px-4">
      <p className="text-3xl lg:text-4xl font-bold text-invert-navy mb-1">
        {formatNumber(display)}{suffix}
      </p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

export default function ProofStatsBar({ className = '' }) {
  const { stats, loading, error } = useNetworkStats();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  if (loading) {
    return (
      <div className={`flex justify-center py-8 ${className}`}>
        <RingLoader color="#dc5024" loading size={32} aria-label="Loading stats" />
      </div>
    );
  }

  if (error || !stats) {
    return null;
  }

  const partnerCount = stats.venuePartners?.length ?? stats.totalBillableScreens ?? 0;
  const advertiserCount = stats.advertisers?.length ?? stats.activeAdvertisers ?? 0;

  return (
    <div ref={ref} className={`rounded-2xl border border-invert-border bg-white py-10 px-6 ${className}`}>
      <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange text-center mb-8">
        Trusted by
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        <AnimatedStat
          label="Active screens"
          value={stats.liveScreens || stats.totalBillableScreens}
          inView={inView}
        />
        <AnimatedStat
          label="Monthly ad plays"
          value={stats.slidesPlayedThisMonth}
          suffix="+"
          inView={inView}
        />
        <AnimatedStat
          label="Venue partners"
          value={partnerCount}
          inView={inView}
        />
        <AnimatedStat
          label="Advertisers"
          value={advertiserCount}
          inView={inView}
        />
      </div>
    </div>
  );
}
